import httpService from './httpService';
import queryString from 'query-string';

import { AnalysisContentModel } from '../../models/AnalysisContentModel';
import { AnalysisContentWidgetModel } from '../../models/AnalysisContentWidgetModel';
import { IBoardDataObject } from '../../models/types/IBoardDataObject';
import { IWidgetStaticData } from './types/IWidgetStaticData';
import { IWidgetCombinedData } from './types/IWidgetCombinedData';
import { IHasBoards } from './types/IHasBoards';
import { IBoardObjectNode } from '../boards/types/IBoardObjectNode';
import { IBoardMeasure } from '../boards/types/IBoardMeasure';
import { IWidgetData } from '../boards/types/IWidgetData';
import { IBoard } from '../boards/types/IBoard';
import { ITimePeriod } from '../boards/types/ITimePeriod';
import { IBoardWidget } from '../boards/types/IBoardWidget';

const _buildValueQueryString = (nodes: IBoardObjectNode[] | null,
                                nodeGroups: IBoardObjectNode[] | null,
                                measures: IBoardMeasure[],
                                endDate: string,
                                singlePeriod: boolean,
                                currency: string,
                                templateProperties: any[] | null = null,
                                includeDescendants: boolean | null = null) => {
  let parsedQuery: any = {};

  if (measures) parsedQuery.measureId = measures && measures.map((x) => x.id);
  if (nodeGroups) parsedQuery.nodeGroupId = nodeGroups && nodeGroups.map((x) => x.id);
  if (nodes) parsedQuery.nodeId = nodes && nodes.map((x) => x.id);

  parsedQuery.endDate = endDate;
  parsedQuery.singlePeriod = singlePeriod;
  parsedQuery.currency = currency;

  if (includeDescendants) {
    parsedQuery.includeDescendants = includeDescendants;
  }

  if (templateProperties) parsedQuery.templatePropertyId = templateProperties.map((x) => x.templatePropertyId);

  return queryString.stringify(parsedQuery);
};

export const getCashflowData = (dashboardId: number, nodeId, valueId, investorId): Promise<any> => {
  let query = { nodeId: {}, valueId: {} };
  query.nodeId = nodeId;
  query.valueId = valueId;
  let cashflowQuery = queryString.stringify(query);

  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/values/cashflows?${cashflowQuery}`);
  return httpService
    .getTyped<any>(endpoint);
};

export const nodeValues = (dashboardId: number,
                           nodes: IBoardObjectNode[] | null,
                           nodeGroups: IBoardObjectNode[] | null,
                           measures: IBoardMeasure[],
                           endDate: string,
                           singlePeriod: boolean,
                           currency: string,
                           investorId: number,
                           includeDescendants?: boolean,
): Promise<IWidgetData[]> => {
  let query = _buildValueQueryString(
    nodes,
    nodeGroups,
    measures,
    endDate,
    singlePeriod,
    currency,
    null,
    includeDescendants);

  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/nodes/values?${query}`);
  return httpService.getTyped<IWidgetData[]>(endpoint);
};

// TODO: We are not using this service - What should we do with it?
export const getAllBoards = (): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/`);
  return httpService.getTyped<any>(endpoint)
    .then((data: IBoardDataObject[]) => {
      return data;
    });
};

export const getBoardsForInvestor = (investorId): Promise<IBoardDataObject[]> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}`);
  return httpService.getTyped<IBoardDataObject[]>(endpoint);
};

export const getAnalysisContent = (dashboardId: number, investorId: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}`);

  return httpService.getTyped<any>(endpoint)
    .then((data: IBoardDataObject) => {
      return new AnalysisContentModel(data);
    });
};

export const getBoard = (boardId: number, investorId: number): Promise<IBoard> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${boardId}`);
  return httpService.getTyped<IBoard>(endpoint);
};

export const getBoardObjectPeriods = (boardId: number | string, investorId: number | string, contentObjectId: number) => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${boardId}/periods/${contentObjectId}`);
  return httpService.getTyped<ITimePeriod[]>(endpoint);
};

// TODO: We are not using this service - What should we do with it?
export const getAllDashboardNotes = (dashboardId: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContentNotes/${dashboardId}`);
  return httpService.getTyped<any>(endpoint);
};

// TODO: We are not using this service - What should we do with it?
export const getDashboardNote = (dashboardId: number, noteId: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContentNotes/${dashboardId}/${noteId}`);
  return httpService.getTyped<any>(endpoint);
};

export const fetchNotes = (dashboardId: number, periodId: number): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContentNotes/${dashboardId}/periods/${periodId}`);
  return httpService.getTyped<any>(endpoint);
};

export const customise = (boardId: string, widgets: IBoardWidget[]): Promise<any> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/${boardId}/customize`);
  let tempWidgetsPayload = [];
  widgets.forEach((widget) =>
    tempWidgetsPayload.push({
      widgetId: widget.id,
      visible: widget.visible,
    }),
  );

  let data = { contentId: parseInt(boardId), widgets: tempWidgetsPayload };
  return httpService.put(endpoint, data);
};

// TODO: This Service is used in the function that we are not using - What should we do about it?
export const nodeStaticValues = (dashboardId: number, nodes, nodeGroups, templateProperties, investorId): Promise<Array<IWidgetStaticData>> => {
  let query = _buildValueQueryString(nodes, nodeGroups, null, null, null, null, null, templateProperties);
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/nodes/values/static?${query}`);
  return httpService.getTyped<Array<IWidgetStaticData>>(endpoint);
};

export const nodeCombinedValues = (
  dashboardId: number,
  nodes: IBoardObjectNode[] | null,
  nodeGroups: IBoardObjectNode[] | null,
  measures: IBoardMeasure[],
  templateProperties: any[],
  endDate: string,
  singlePeriod: boolean,
  currency: string,
  investorId: number,
  includeDescendants?: boolean,
): Promise<IWidgetCombinedData> => {
  let query = _buildValueQueryString(
    nodes,
    nodeGroups,
    measures,
    endDate,
    singlePeriod,
    currency,
    templateProperties,
    includeDescendants,
  );

  let endpoint = httpService.getEndpointURL(`/AnalysisContent/investors/${investorId}/${dashboardId}/nodes/values/measuresAndStatic?${query}`);
  return httpService.getTyped<IWidgetCombinedData>(endpoint);
};

export const hasBoards = (): Promise<IHasBoards> => {
  let endpoint = httpService.getEndpointURL(`/AnalysisContent/hasBoards`);
  return httpService.getTyped<IHasBoards>(endpoint);
};
